import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { SessionWarningEvent } from "data/@types/SessionManagement";
import { tokenRefreshManager } from "data/services/session/TokenRefreshManager";
import React, { useCallback, useEffect, useState } from "react";

export const InactivityWarningModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    (Number(import.meta.env.VITE_TOKEN_WARNING_THRESHOLD) || 30) * 60,
  );
  const [isExtending, setIsExtending] = useState(false);

  const handleShowModal = useCallback((event: Event) => {
    const customEvent = event as SessionWarningEvent;
    setSecondsRemaining(customEvent.detail.secondsRemaining);
    setOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCountdownUpdate = useCallback((event: Event) => {
    const customEvent = event as SessionWarningEvent;
    const newValue = customEvent.detail.secondsRemaining;
    setSecondsRemaining(newValue);
  }, []);

  useEffect(() => {
    globalThis.addEventListener("show-inactivity-modal", handleShowModal);
    globalThis.addEventListener("close-inactivity-modal", handleCloseModal);
    globalThis.addEventListener("update-countdown", handleCountdownUpdate);

    return () => {
      globalThis.removeEventListener("show-inactivity-modal", handleShowModal);
      globalThis.removeEventListener(
        "close-inactivity-modal",
        handleCloseModal,
      );
      globalThis.removeEventListener("update-countdown", handleCountdownUpdate);
    };
  }, [handleShowModal, handleCloseModal, handleCountdownUpdate]);

  useEffect(() => {
    if (open && secondsRemaining === 0) {
      tokenRefreshManager.logout();
    }
  }, [open, secondsRemaining]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      await tokenRefreshManager.extendSession();
      setOpen(false);
    } catch (error) {
      console.error("Failed to extend session:", error);
    } finally {
      setIsExtending(false);
    }
  };

  const handleExit = async () => {
    setOpen(false);
    await tokenRefreshManager.logout();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isExpired = secondsRemaining <= 0;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      data-test-id="inactivity-warning-modal"
    >
      <DialogTitle>Your session is about to expire</DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 16, lineHeight: 1.6 }}>
          {isExpired ? (
            <>
              Your session has expired ({" "}
              <span data-test-id="countdown-timer">
                {formatTime(secondsRemaining)}
              </span>
              ).
              <br />
              You will be logged out automatically.
            </>
          ) : (
            <>
              Your session will expire in{" "}
              <strong data-test-id="countdown-timer">
                {formatTime(secondsRemaining)}
              </strong>
              .
              <br />
              Extend your session to stay logged in.
            </>
          )}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleExtendSession}
          variant="outlined"
          data-test-id="inactivity-modal-extend-btn"
          disabled={isExtending || isExpired}
        >
          {isExtending ? "Extending..." : "Extend my session"}
        </Button>
        <Button
          onClick={handleExit}
          variant="contained"
          color="error"
          data-test-id="inactivity-modal-exit-btn"
          disabled={isExtending}
        >
          Exit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
